import { useEffect, useRef } from 'react';
import { getHeight, getOuterHeight, getOuterWidth, getWidth } from './utils';

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {React.CSSProperties} [props.style]
 * @param {string[]} props.panelSizes
 * @param {string[][]} [props.snapPanelSizes]
 * @param {boolean} [props.horizontal]
 * @param {React.ReactNode} [props.gutterSlot]
 * @param {boolean} [props.disableDrag]
 * @param {() => void} [props.onResizeStart]
 * @param {(event: { snapIndex: number }) => void} [props.onResizeEnd]
 * @param {React.UIEventHandler} [props.onHandleClick]
 * @returns {React.JSX.Element | undefined}
 */
export function Splitter({
  children,
  style,
  panelSizes,
  snapPanelSizes,
  horizontal = false,
  gutterSlot,
  disableDrag = false,
  onResizeStart,
  onResizeEnd,
  onHandleClick,
}) {
  /** @type {React.RefObject<HTMLDivElement>} */
  // @ts-ignore
  const container = useRef(null);

  /** @type {React.RefObject<HTMLDivElement>} */
  // @ts-ignore
  const prevPanel = useRef(null);

  /** @type {React.RefObject<HTMLDivElement>} */
  // @ts-ignore
  const nextPanel = useRef(null);

  /** @type {React.RefObject<HTMLDivElement>} */
  // @ts-ignore
  const handle = useRef(null);

  /** @type {React.RefObject<boolean>} */
  const isDragging = useRef(false);

  /** @type {React.RefObject<number>} */
  const startPos = useRef(0);

  /** @type {React.RefObject<number>} */
  const size = useRef(0);

  /** @type {React.RefObject<number>} */
  const prevPanelSize = useRef(0);

  /** @type {React.RefObject<number>} */
  const nextPanelSize = useRef(0);

  /** @type {React.RefObject<string | null>} */
  const pointerType = useRef(null);
  const mouseDownPos = useRef(0);
  const mouseUpPos = useRef(0);
  const gutterSize = useRef(0);
  const dimension = horizontal ? 'width' : 'height';
  const direction = horizontal ? 'row' : 'column';
  const prevPanelFlexBasis = panelSizes?.[0]?.toString() ?? '50%';
  const nextPanelFlexBasis = panelSizes?.[1]?.toString() ?? '50%';
  const snapPercents = snapPanelSizes?.map((sizes) => Number(sizes[1].replace('%', '')));

  useEffect(() => {
    gutterSize.current = handle.current.getBoundingClientRect()[dimension];
  }, [dimension]);

  useEffect(() => {
    prevPanel.current.style.flexBasis = prevPanelFlexBasis;
    nextPanel.current.style.flexBasis = nextPanelFlexBasis;
  }, [prevPanelFlexBasis, nextPanelFlexBasis]);

  const findNearestSnapPanelSizesIndex = () => {
    if (!snapPercents?.length) {
      return -1;
    }

    // Find the snap percent nearest to the current
    const currentPercent = Number(nextPanel.current.style.flexBasis.replace('%', ''));
    const snap = snapPercents.reduce(
      (currentSnap, snapPercent, index) => {
        if (
          currentSnap.nearestPercent == null ||
          Math.abs(snapPercent - currentPercent) < Math.abs(currentSnap.nearestPercent - currentPercent)
        ) {
          return {
            nearestPercent: snapPercent,
            snapIndex: index,
          };
        }
        return currentSnap;
      },
      { nearestPercent: /** @type {number | null} */ (null), snapIndex: -1 }
    );
    return snap.snapIndex;
  };

  /** @type {(event: React.TouchEvent | React.MouseEvent) => void} */
  const startResize = (event) => {
    onResizeStart?.();

    isDragging.current = true;
    size.current = horizontal ? getWidth(container.current) : getHeight(container.current);
    startPos.current = horizontal
      ? 'changedTouches' in event
        ? event.changedTouches[0].pageX
        : event.pageX
      : 'changedTouches' in event
        ? event.changedTouches[0].pageY
        : event.pageY;
    prevPanelSize.current =
      (100 * (horizontal ? getOuterWidth(prevPanel.current) : getOuterHeight(prevPanel.current))) / size.current;
    nextPanelSize.current =
      (100 * (horizontal ? getOuterWidth(nextPanel.current) : getOuterHeight(nextPanel.current))) / size.current;
  };

  /** @type {(event: TouchEvent | MouseEvent) => void} */
  const onResize = (event) => {
    if (!prevPanel.current || !nextPanel.current) {
      return;
    }

    const pos = horizontal
      ? 'changedTouches' in event
        ? event.changedTouches[0].pageX
        : event.pageX
      : 'changedTouches' in event
        ? event.changedTouches[0].pageY
        : event.pageY;

    const newPos = ((pos - startPos.current) * 100) / size.current;
    const newPrevPanelSize = Math.min(size.current - gutterSize.current, Math.max(0, prevPanelSize.current + newPos));
    const newNextPanelSize = Math.min(size.current - gutterSize.current, Math.max(0, nextPanelSize.current - newPos));
    prevPanel.current.style.flexBasis = newPrevPanelSize + '%';
    nextPanel.current.style.flexBasis = newNextPanelSize + '%';
  };

  /** @type {(event: TouchEvent | MouseEvent) => void} */
  const endResize = () => {
    startPos.current = 0;
    isDragging.current = false;

    const snapIndex = findNearestSnapPanelSizesIndex();
    if (snapIndex >= 0 && snapPercents) {
      const snapPercent = snapPercents[snapIndex];
      const newPrev = 100 - snapPercent;
      const newNext = snapPercent;
      prevPanel.current.style.flexBasis = newPrev + '%';
      nextPanel.current.style.flexBasis = newNext + '%';
    }

    onResizeEnd?.({ snapIndex });
  };

  /** @type {(event: React.MouseEvent) => void} */
  const handleMouseDown = (event) => {
    if (disableDrag || isDragging.current) {
      return;
    }

    event.preventDefault();
    pointerType.current = 'mouse';
    mouseDownPos.current = horizontal ? event.pageX : event.pageY;
    startResize(event);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
  };

  /** @type {(event: MouseEvent) => void} */
  const handleMouseMove = (event) => {
    onResize(event);
  };

  /** @type {(event: MouseEvent) => void} */
  const handleMouseUp = (event) => {
    mouseUpPos.current = horizontal ? event.pageX : event.pageY;
    endResize(event);
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('mousemove', handleMouseMove);
  };

  /** @type {(event: React.TouchEvent) => void} */
  const handleTouchStart = (event) => {
    if (disableDrag || isDragging.current) {
      return;
    }

    pointerType.current = 'touch';
    startResize(event);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchmove', handleTouchMove);
  };

  /** @type {(event: TouchEvent) => void} */
  const handleTouchMove = (event) => {
    onResize(event);
  };

  /** @type {(event: TouchEvent) => void} */
  const handleTouchEnd = (event) => {
    endResize(event);
    window.removeEventListener('touchend', handleTouchEnd);
    window.removeEventListener('touchmove', handleTouchMove);
  };

  const handleHandleClick = (/** @type {React.MouseEvent} */ event) => {
    if (pointerType.current !== 'mouse' || mouseDownPos.current === mouseUpPos.current) {
      onHandleClick?.(event);
    }
  };

  const handleHandleKeyUp = (/** @type {React.KeyboardEvent} */ event) => {
    if (event.code === 'Space' || event.key === 'Space') {
      // Provides start/end events consistent with mouse/touch events
      onResizeStart?.();
      onHandleClick?.(event);
      onResizeEnd?.({ snapIndex: -1 });
    }
  };

  return children ? (
    <div
      ref={container}
      style={{
        display: 'flex',
        flexDirection: direction,
        height: '100%',
        width: '100%',
        position: 'relative',
        ...style,
      }}
    >
      <div ref={prevPanel} style={{ position: 'relative', flexBasis: prevPanelFlexBasis }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          {
            // @ts-ignore
            children[0]
          }
        </div>
      </div>
      <div
        ref={handle}
        role="button"
        tabIndex={0}
        onKeyUp={handleHandleKeyUp}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleHandleClick}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          boxShadow: '4px 0 40px 0 rgba(0, 0, 0, 0.25)',
          zIndex: 1,
        }}
        aria-label="Resize panel"
      >
        <div style={{ height: '100%', width: '100%', background: '#fff' }}>
          <div
            style={{
              height: '4px',
              width: '38px',
              margin: '8px auto',
              background: '#ccc',
              borderRadius: '4px',
              userSelect: 'none',
            }}
          />
        </div>
        <div style={{ [dimension]: '100%' }}>{gutterSlot}</div>
      </div>
      <div ref={nextPanel} style={{ position: 'relative', flexBasis: nextPanelFlexBasis }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          {
            // @ts-ignore
            children[1]
          }
        </div>
      </div>
    </div>
  ) : undefined;
}
